import {tap} from 'rxjs/operators';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';

import {Injectable} from '@angular/core';
import {DialogService} from 'app/common/dialog/dialog-service';
import {ERROR_DIALOG_CLASS} from 'app/common/dialog/dialog-error';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {REGEX_GOOGLE_SHEET_URL} from 'app/app.constants';
import {ERROR_SEVERITY_TOKEN, ErrorSeverity} from 'app/blocks/util/request-util';
import {AlertService} from 'app/common/alert/alert.service';
import {isAssistantApiUrl} from 'app/blocks/util/assistant-url';

@Injectable({
    providedIn: 'root'
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
    constructor(
        private dialogService: DialogService,
        private alertService: AlertService
    ) {}

    readonly ignoreCases = [
        {url: 'traffic-areas-for-postcode-area', status: 404},
        {url: '/api/company', status: 404}
    ];

    readonly errorHints: Array<{urlPattern: string; field?: string; messagePattern?: string; hint: string}> = [
        {
            urlPattern: '/api/drivers',
            field: 'country',
            hint: "Set Default Country in Tenant settings or add Country in the Driver's Contact Info."
        }
    ];

    modalRef!: NzModalRef;

    private getHint(url: string | null | undefined, fieldErrors?: any[]): string | undefined {
        for (const rule of this.errorHints) {
            if (!url?.includes(rule.urlPattern)) continue;
            if (rule.field && !fieldErrors?.some((fe) => fe.field?.toLowerCase().includes(rule.field!.toLowerCase()))) continue;
            if (rule.messagePattern && !fieldErrors?.some((fe) => fe.message?.toLowerCase().includes(rule.messagePattern!.toLowerCase()))) continue;
            return rule.hint;
        }
        return undefined;
    }

    private getMessageFromTextBody(body: string): string {
        try {
            const json = JSON.parse(body);
            return json?.detail ?? json?.title ?? json?.message ?? 'common.errorMessage';
        } catch {
            // Plain-text body (e.g. from a servlet filter): show short text as-is rather than a JSON
            // parse error, but never an HTML error page from a proxy/load balancer.
            const text = body?.trim();
            return text && !text.startsWith('<') && text.length <= 200 ? text : 'common.errorMessage';
        }
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap(
                (event: HttpEvent<any>) => {},
                (err: any) => {
                    if (err.status === 0 && err.error instanceof ProgressEvent) {
                        // A client-side or network error occurred. Handle it accordingly.
                        console.log('Client side error:', err.error);
                        this.alertService.showWarning('common.networkErrorMessage', 'common.networkError', {
                            timeOut: 30000,
                            extendedTimeOut: 0,
                            closeButton: true,
                            tapToDismiss: false
                        });
                    } else if (err instanceof HttpErrorResponse) {
                        if (isAssistantApiUrl(request.url) || isAssistantApiUrl(err.url)) {
                            return; // D-502: the CoPilot chat reports its own errors
                        }
                        if (err.error instanceof Blob) {
                            // A blob request that opted out of the error modal (e.g. AI template PDF preview)
                            // should fail silently — its caller surfaces the error inline.
                            if (request.context?.get(ERROR_SEVERITY_TOKEN) === ErrorSeverity.IGNORE) {
                                return;
                            }
                            // https://github.com/angular/angular/issues/19888
                            // When request of type Blob, the error is also in Blob instead of object of the json data
                            return new Promise<any>((resolve, reject) => {
                                let reader = new FileReader();
                                reader.onload = (e: Event) => {
                                    try {
                                        const err = JSON.parse((<any>e.target).result);
                                        const blobFieldErrors: any[] = err.fieldErrors ?? err.violations;
                                        let blobMessage: string;
                                        if (blobFieldErrors?.length > 0) {
                                            const items = blobFieldErrors
                                                .map((fe) => `<li><strong>${fe.field}</strong>: ${fe.message}</li>`)
                                                .join('');
                                            blobMessage = `<ul style="margin:0;padding-left:1.2em">${items}</ul>`;
                                        } else {
                                            blobMessage = err.detail ?? 'common.errorMessage';
                                        }
                                        this.dialogService
                                            .showMessage(
                                                {
                                                    message: blobMessage,
                                                    title: err.title ?? 'common.error',
                                                    confirmText: 'common.ok'
                                                },
                                                {},
                                                {nzClassName: ERROR_DIALOG_CLASS},
                                                this.modalRef
                                            )
                                            .then((ref) => {
                                                this.modalRef = ref;
                                            });
                                    } catch (e) {
                                        console.log(e);
                                        reject(err);
                                    }
                                };
                                reader.onerror = (e) => {
                                    console.log(e);
                                    reject(err);
                                };
                                reader.readAsText(err.error);
                            });
                        }
                        if (!(
                            err.status === 401 ||
                            err.message === '' ||
                            (err.url &&
                                (err.url.indexOf('/api/account') > 0 ||
                                    err.url.indexOf('/api/company/logo') > 0 ||
                                    err.url.indexOf('/LocalSetting') > 0)) ||
                            REGEX_GOOGLE_SHEET_URL.test(err.url ?? '')
                        )) {
                            console.log('[ErrorHandlerInterceptor] There was an http error: ', err);
                            console.log('URL', err.url);
                            const fieldErrors: any[] = err.error?.fieldErrors ?? err.error?.violations;
                            let message: string;
                            if (fieldErrors?.length > 0) {
                                const items = fieldErrors.map((fe) => `<li><strong>${fe.field}</strong>: ${fe.message}</li>`).join('');
                                message = `<ul style="margin:0;padding-left:1.2em">${items}</ul>`;
                            } else if (typeof err.error === 'string') {
                                message = this.getMessageFromTextBody(err.error);
                            } else {
                                message = err.error?.detail ?? err.error?.message;
                            }
                            let title: string = err.error?.title;
                            let params = {};
                            if (err.status === 429) {
                                // The API rate limiter answers with a plain-text "Too many requests" body.
                                title = 'common.tooManyRequests';
                                message = 'common.tooManyRequestsMessage';
                                params = {seconds: err.headers?.get('X-Rate-Limit-Retry-After-Seconds') || 60};
                            }
                            if (err.url) {
                                for (const ignoreCase of this.ignoreCases) {
                                    if (err.url.includes(ignoreCase.url) && err.status === ignoreCase.status) {
                                        return;
                                    }
                                }
                            }
                            const errorSeverity = request.context?.get(ERROR_SEVERITY_TOKEN);

                            if (errorSeverity === ErrorSeverity.ERROR) {
                                const hint = this.getHint(err.url, fieldErrors);
                                this.dialogService
                                    .showMessage(
                                        {
                                            message: message ?? 'common.errorMessage',
                                            title: title ?? 'common.error',
                                            hint,
                                            confirmText: 'common.ok'
                                        },
                                        params,
                                        // API errors always get the ask link, whatever title the server sent
                                        {nzClassName: ERROR_DIALOG_CLASS},
                                        this.modalRef
                                    )
                                    .then((ref) => {
                                        this.modalRef = ref;
                                    });
                            } else if (errorSeverity === ErrorSeverity.WARN) {
                                this.alertService.showWarning(message ?? 'common.backgroundErrorMessage', title ?? 'common.backgroundError', {
                                    timeOut: 30000,
                                    extendedTimeOut: 0,
                                    closeButton: true,
                                    tapToDismiss: false
                                });
                            } else if (errorSeverity === ErrorSeverity.IGNORE) {
                                return;
                            }
                        }
                    }
                }
            )
        );
    }
}
